import { useState, useRef } from "react"
import { router, usePage } from "@inertiajs/react"
import { track } from "@/lib/analytics"
import type { SharedProps, SocialConnection } from "@/types"
import { csrfToken } from "@/lib/csrf"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import PlatformCaptionTabs from "@/components/dashboard/posts/platform-caption-tabs"
import type { LocationMeta } from "@/components/dashboard/posts/location-picker"
import { Switch } from "@/components/ui/switch"
import { Label } from "@/components/ui/label"
import { DateTimePicker } from "@/components/ui/date-time-picker"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
type Platform = "instagram" | "facebook"

const PLATFORM_LABELS: Record<Platform, string> = {
  instagram: "Instagram",
  facebook: "Facebook",
}

export default function NewPostDialog({
  connections,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
}: {
  connections: SocialConnection[]
  open?: boolean
  onOpenChange?: (open: boolean) => void
}) {
  const { currentUser } = usePage<SharedProps>().props
  const [internalOpen, setInternalOpen] = useState(false)
  const open = controlledOpen ?? internalOpen
  const setOpen = controlledOnOpenChange ?? setInternalOpen
  const [selectedPlatforms, setSelectedPlatforms] = useState<Set<Platform>>(new Set())
  const [captions, setCaptions] = useState<Record<Platform, string>>({
    instagram: "",
    facebook: "",
  })
  const [imageFile, setImageFile] = useState<File | null>(null)
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [scheduleEnabled, setScheduleEnabled] = useState(false)
  const [scheduledAt, setScheduledAt] = useState<Date | undefined>(undefined)
  const [publishing, setPublishing] = useState(false)
  const [generatingCaption, setGeneratingCaption] = useState<Record<string, boolean>>({})
  const [facebookPageId, setFacebookPageId] = useState<string>("")
  const [locationId, setLocationId] = useState<string | null>(null)
  const [locationName, setLocationName] = useState<string | null>(null)
  const [locationMeta, setLocationMeta] = useState<LocationMeta | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const fbConnection = connections.find(
    (c) => c.platform === "facebook" && c.status === "active",
  )
  const fbPages = fbConnection?.managed_pages || []

  const supportedConnections = connections.filter(
    (c) => (c.platform === "facebook" || c.platform === "instagram") && c.status === "active",
  )

  function togglePlatform(platform: Platform) {
    setSelectedPlatforms((prev) => {
      const next = new Set(prev)
      if (next.has(platform)) {
        next.delete(platform)
      } else {
        next.add(platform)
      }
      return next
    })
  }

  function handleImageChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return

    setImageFile(file)
    const reader = new FileReader()
    reader.onload = () => setImagePreview(reader.result as string)
    reader.readAsDataURL(file)
  }

  function removeImage() {
    setImageFile(null)
    setImagePreview(null)
    if (fileInputRef.current) fileInputRef.current.value = ""
  }

  async function handlePublish() {
    if (selectedPlatforms.size === 0) return

    // Instagram requires an image
    if (selectedPlatforms.has("instagram") && !imageFile) {
      toast.error("Instagram requires an image for non-video posts.")
      return
    }

    setPublishing(true)
    try {
      const formData = new FormData()
      for (const p of selectedPlatforms) {
        formData.append("platforms[]", p)
        formData.append(`captions[${p}]`, captions[p])
      }
      if (scheduleEnabled && scheduledAt) {
        formData.append("scheduled_at", scheduledAt.toISOString())
      }
      if (selectedPlatforms.has("facebook") && facebookPageId) {
        formData.append("facebook_page_id", facebookPageId)
      }
      if (imageFile) {
        formData.append("image", imageFile)
      }
      if (locationId) {
        formData.append("location_id", locationId)
      }
      if (locationName) {
        formData.append("location_name", locationName)
      }
      if (locationMeta?.address) {
        formData.append("location_address", locationMeta.address)
      }
      if (locationMeta?.verified) {
        formData.append("location_verified", "true")
      }
      if (locationMeta?.picture_url) {
        formData.append("location_picture_url", locationMeta.picture_url)
      }
      if (locationMeta?.hours) {
        formData.append("location_hours", locationMeta.hours)
      }
      const response = await fetch("/dashboard/posts", {
        method: "POST",
        headers: {
          "X-CSRF-Token": csrfToken(),
        },
        body: formData,
      })

      if (response.ok) {
        track("standalone_post_published", {
          platforms: Array.from(selectedPlatforms),
          scheduled: scheduleEnabled,
          has_image: !!imageFile,
        })
        toast.success(
          scheduleEnabled
            ? "Posts scheduled successfully"
            : "Publishing to social media...",
        )
        setOpen(false)
        setSelectedPlatforms(new Set())
        setCaptions({ instagram: "", facebook: "" })
        removeImage()
        router.reload()
      } else {
        const data = await response.json()
        toast.error(data.error || "Failed to create posts")
      }
    } catch {
      toast.error("Something went wrong. Please try again.")
    } finally {
      setPublishing(false)
    }
  }

  async function handleGenerateCaption(platform: string) {
    setGeneratingCaption((prev) => ({ ...prev, [platform]: true }))
    try {
      const response = await fetch("/dashboard/posts/generate_caption", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken(),
        },
        body: JSON.stringify({ platform }),
      })

      if (response.ok) {
        const data = await response.json()
        setCaptions((prev) => ({ ...prev, [platform]: data.caption }))
        track("standalone_caption_generated", { platform })
      } else {
        const data = await response.json()
        toast.error(data.error || "Failed to generate caption")
      }
    } catch {
      toast.error("Something went wrong. Please try again.")
    } finally {
      setGeneratingCaption((prev) => ({ ...prev, [platform]: false }))
    }
  }

  const hasConnections = supportedConnections.length > 0

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {controlledOpen === undefined && (
        <DialogTrigger asChild>
          <Button className="bg-red text-white hover:bg-deep-red">
            <svg className="mr-1 size-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
            </svg>
            New post
          </Button>
        </DialogTrigger>
      )}

      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>New post</DialogTitle>
          <DialogDescription>
            Create a new social media post with an image and caption.
          </DialogDescription>
        </DialogHeader>

        {!hasConnections ? (
          <NoConnectionsCallout />
        ) : (<>

        {/* Image upload */}
        <div className="space-y-2 py-2">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-warm-grey">
            Image
          </p>
          {imagePreview ? (
            <div className="relative">
              <img
                src={imagePreview}
                alt="Upload preview"
                className="max-h-48 w-full rounded-lg border border-stone/30 object-cover"
              />
              <button
                type="button"
                onClick={removeImage}
                className="absolute right-2 top-2 rounded-full bg-charcoal/70 p-1 text-white transition-colors hover:bg-charcoal"
              >
                <svg className="size-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="flex w-full items-center justify-center gap-2 rounded-lg border-2 border-dashed border-stone/40 px-4 py-8 text-sm text-warm-grey transition-colors hover:border-stone/60 hover:bg-stone/5"
            >
              <svg className="size-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0 0 22.5 18.75V5.25a2.25 2.25 0 0 0-2.25-2.25H3.75A2.25 2.25 0 0 0 1.5 5.25v13.5A2.25 2.25 0 0 0 3.75 21Z" />
              </svg>
              Click to upload an image
            </button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={handleImageChange}
            className="hidden"
          />
          <p className="text-[10px] text-warm-grey/60">
            Instagram requires an image. Facebook supports text-only posts.
          </p>
        </div>

        {/* Platform selection */}
        <div className="space-y-3 py-2">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-warm-grey">
            Platforms
          </p>
          <div className="grid grid-cols-2 gap-2">
            {(["facebook", "instagram"] as Platform[]).map((platform) => {
              const connection = supportedConnections.find(
                (c) => c.platform === platform,
              )
              const isSelected = selectedPlatforms.has(platform)
              const disabled = !connection

              return (
                <button
                  key={platform}
                  type="button"
                  onClick={() => !disabled && togglePlatform(platform)}
                  disabled={disabled}
                  className={`flex items-center gap-2.5 rounded-lg border px-3 py-2.5 text-left text-sm transition-all ${
                    disabled
                      ? "cursor-not-allowed border-stone/40 bg-stone/5 text-warm-grey/40"
                      : isSelected
                        ? "border-red/50 bg-red/5 text-charcoal"
                        : "border-stone/60 text-warm-grey hover:border-stone"
                  }`}
                >
                  <span
                    className={`flex size-4 items-center justify-center rounded border text-[10px] ${
                      isSelected
                        ? "border-red bg-red text-white"
                        : "border-stone/70"
                    }`}
                  >
                    {isSelected && (
                      <svg className="size-2.5" fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                      </svg>
                    )}
                  </span>
                  <span className="font-medium">
                    {PLATFORM_LABELS[platform]}
                  </span>
                  {!connection && (
                    <span className="ml-auto text-[10px] text-warm-grey/50">
                      Not connected
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        </div>

        {/* Captions */}
        {selectedPlatforms.size > 0 && (
          <PlatformCaptionTabs
            platforms={Array.from(selectedPlatforms)}
            captions={captions}
            onCaptionChange={(platform, value) =>
              setCaptions((prev) => ({ ...prev, [platform]: value }))
            }
            onGenerateCaption={handleGenerateCaption}
            generatingCaption={generatingCaption}
            facebookPages={fbPages}
            facebookPageId={facebookPageId}
            onFacebookPageChange={setFacebookPageId}
            locationId={locationId}
            locationName={locationName}
            locationMeta={locationMeta}
            onLocationChange={(id, name, meta) => {
              setLocationId(id)
              setLocationName(name)
              setLocationMeta(meta ?? null)
            }}
            showLocationPicker={!!currentUser?.is_superadmin}
          />
        )}

        {/* Schedule toggle */}
        {selectedPlatforms.size > 0 && (
          <div className="space-y-3 border-t border-stone/20 pt-4">
            <div className="flex items-center gap-2">
              <Switch
                id="post-schedule-toggle"
                checked={scheduleEnabled}
                onCheckedChange={setScheduleEnabled}
              />
              <Label htmlFor="post-schedule-toggle" className="text-sm">
                Schedule for later
              </Label>
            </div>

            {scheduleEnabled && (
              <DateTimePicker
                value={scheduledAt}
                onChange={setScheduledAt}
                minDate={new Date()}
                placeholder="Choose date & time"
                className="w-auto"
              />
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handlePublish}
            disabled={
              publishing ||
              selectedPlatforms.size === 0 ||
              (scheduleEnabled && !scheduledAt)
            }
            className="bg-red px-6 text-white hover:bg-red/90"
          >
            {publishing
              ? "Publishing..."
              : scheduleEnabled
                ? "Schedule"
                : `Publish to ${selectedPlatforms.size} platform${selectedPlatforms.size !== 1 ? "s" : ""}`}
          </Button>
        </DialogFooter>

        </>)}
      </DialogContent>
    </Dialog>
  )
}

function NoConnectionsCallout() {
  return (
    <div className="rounded-lg border border-stone/30 bg-stone/5 px-4 py-5 text-center">
      <svg className="mx-auto mb-2 size-8 text-warm-grey/40" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244" />
      </svg>
      <p className="text-sm font-medium text-charcoal">No social accounts connected</p>
      <p className="mt-1 text-xs text-warm-grey">
        Connect your social media accounts to start publishing.
      </p>
      <a
        href="/dashboard/connections"
        className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-red px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-deep-red"
      >
        Connect accounts
        <svg className="size-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
        </svg>
      </a>
    </div>
  )
}
